import React, { useMemo, useState } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { ListSortMenu, type ListSortOption } from '@/shared/components/ListSortMenu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { useProjectUsers } from '../hooks';
import {
  RFI_PRIORITY_LABELS,
  RFI_STATUS_LABELS,
  type RFIFilters,
  type RFIPriority,
  type RFISortKey,
  type RFISortState,
  type RFIStatus,
} from '../types';

interface Props {
  projectId: number | undefined;
  filters: RFIFilters;
  onFiltersChange: (filters: RFIFilters) => void;
  totalCount: number;
  filteredCount: number;
  sort: RFISortState[];
  onSortChange: (sort: RFISortState[]) => void;
}

const SORT_OPTIONS: readonly ListSortOption<RFISortKey>[] = [
  { key: 'rfi_number', label: 'RFI number' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'responsible_party', label: 'Responsible party' },
  { key: 'due_date', label: 'Due date' },
  { key: 'created_at', label: 'Created' },
];

export const RfisFilters: React.FC<Props> = ({
  projectId,
  filters,
  onFiltersChange,
  totalCount,
  filteredCount,
  sort,
  onSortChange,
}) => {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const { data: users = [] } = useProjectUsers(projectId);

  const activeFilterCount = useMemo(
    () =>
      [filters.status, filters.priority, filters.responsible_party, filters.overdue].filter(Boolean)
        .length,
    [filters.overdue, filters.priority, filters.responsible_party, filters.status]
  );
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.search ||
          filters.status ||
          filters.priority ||
          filters.responsible_party ||
          filters.overdue
      ),
    [filters]
  );

  const update = (patch: Partial<RFIFilters>) => onFiltersChange({ ...filters, ...patch });
  const clearDialogFilters = () =>
    onFiltersChange(filters.search ? { search: filters.search } : {});

  return (
    <div className="space-y-3" data-tour-id="rfis-filters">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 md:max-w-[360px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by number, title, drawing…"
            value={filters.search ?? ''}
            onChange={(e) => update({ search: e.target.value || undefined })}
            className="w-full pl-8"
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="hidden gap-2 md:inline-flex"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>

        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="hidden md:inline-flex"
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFiltersChange({})}
            className="hidden gap-1 md:inline-flex"
          >
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        )}

        <span className="ml-auto hidden text-sm text-muted-foreground md:inline">
          Showing {filteredCount} of {totalCount}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 px-3 py-2 md:hidden">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-2"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>
        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="w-full"
        />
      </div>

      <div className="flex items-center justify-between gap-3 md:hidden">
        {hasActiveFilters ? (
          <Button variant="ghost" size="sm" onClick={() => onFiltersChange({})} className="gap-1">
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        ) : (
          <span />
        )}
        <span className="text-sm text-muted-foreground">
          Showing {filteredCount} of {totalCount}
        </span>
      </div>

      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>RFI Filters</DialogTitle>
            <DialogDescription>Refine the RFIs shown on this page.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <FilterField label="Status">
              <Select
                value={filters.status ?? 'all'}
                onValueChange={(value) =>
                  update({ status: value === 'all' ? undefined : (value as RFIStatus) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {(Object.keys(RFI_STATUS_LABELS) as RFIStatus[]).map((status) => (
                    <SelectItem key={status} value={status}>
                      {RFI_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Priority">
              <Select
                value={filters.priority ?? 'all'}
                onValueChange={(value) =>
                  update({ priority: value === 'all' ? undefined : (value as RFIPriority) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  {(Object.keys(RFI_PRIORITY_LABELS) as RFIPriority[]).map((priority) => (
                    <SelectItem key={priority} value={priority}>
                      {RFI_PRIORITY_LABELS[priority]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Responsible Party">
              <Select
                value={filters.responsible_party ? String(filters.responsible_party) : 'all'}
                onValueChange={(value) =>
                  update({ responsible_party: value === 'all' ? undefined : Number(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All responsible parties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All responsible parties</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Due Status">
              <Select
                value={filters.overdue ? 'yes' : 'all'}
                onValueChange={(value) => update({ overdue: value === 'yes' ? true : undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All due dates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All due dates</SelectItem>
                  <SelectItem value="yes">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>
          </div>

          <DialogFooter className="gap-2 sm:space-x-0">
            <Button
              variant="outline"
              onClick={clearDialogFilters}
              disabled={activeFilterCount === 0}
            >
              Clear All
            </Button>
            <DialogClose asChild>
              <Button>Done</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export default RfisFilters;
